import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';

type Exercise = {
  name: string;
  sets?: number;
  reps?: string;
  weight?: string;
  rest?: string;
};

type Routine = {
  goal?: string;
  level?: string;
  days?: string[];
  exercises?: Exercise[];
  [key: string]: unknown;
};

type SavedRoutine = Routine & {
  id: number;
  savedAt: string;
};

type TrainingContextValue = {
  generatedRoutine: Routine | null;
  setGeneratedRoutine: (routine: Routine | null) => void;
  savedRoutines: SavedRoutine[];
  saveRoutine: (routine: Routine) => void;
  clearCurrentRoutine: () => void;
};

const TrainingContext = createContext<TrainingContextValue | null>(null);

export const useTraining = () => {
  const context = useContext(TrainingContext);
  if (!context) {
    throw new Error('useTraining must be used within TrainingProvider');
  }
  return context;
};

type TrainingProviderProps = {
  children: ReactNode;
};

export const TrainingProvider = ({ children }: TrainingProviderProps) => {
  const [generatedRoutine, setGeneratedRoutine] = useState<Routine | null>(null);
  const [savedRoutines, setSavedRoutines] = useState<SavedRoutine[]>([]);

  // Load saved routines from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('trainingRoutines');
    if (saved) {
      setSavedRoutines(JSON.parse(saved));
    }

    // Also check for current routine
    const current = localStorage.getItem('currentRoutine');
    if (current) {
      setGeneratedRoutine(JSON.parse(current));
    }
  }, []);

  const saveRoutine = (routine: Routine) => {
    const newRoutine = {
      ...routine,
      id: Date.now(),
      savedAt: new Date().toISOString(),
    };
    const updated = [newRoutine, ...savedRoutines].slice(0, 10); // Keep last 10
    setSavedRoutines(updated);
    localStorage.setItem('trainingRoutines', JSON.stringify(updated));
    localStorage.setItem('currentRoutine', JSON.stringify(routine));
  };

  const setCurrentRoutine = (routine: Routine | null) => {
    setGeneratedRoutine(routine);
    localStorage.setItem('currentRoutine', JSON.stringify(routine));
  };

  const clearCurrentRoutine = () => {
    setGeneratedRoutine(null);
    localStorage.removeItem('currentRoutine');
  };

  return (
    <TrainingContext.Provider
      value={{
        generatedRoutine,
        setGeneratedRoutine: setCurrentRoutine,
        savedRoutines,
        saveRoutine,
        clearCurrentRoutine,
      }}
    >
      {children}
    </TrainingContext.Provider>
  );
};
